import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format, subDays } from 'date-fns';
import {
  BarChart3,
  Users,
  TrendingUp,
  DollarSign,
  Mail,
  Phone,
  Calendar,
  CheckCircle2,
  ArrowRight,
  ClipboardList,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { PilotCrmService } from '@/services/pilot-crm.service';
import {
  PilotCrmAnalyticsService,
  DateRange,
} from '@/services/pilot-crm-analytics.service';
import { MetricsCard } from '@/components/pilot-crm/MetricsCard';
import { PipelineFunnel } from '@/components/pilot-crm/PipelineFunnel';
import { ActivityFeed } from '@/components/pilot-crm/ActivityFeed';
import { ConversionChart, SourceBreakdownChart } from '@/components/pilot-crm/ConversionChart';
import {
  Button,
  Card,
  FormSelect,
  LoadingSpinner,
  SectionHeader,
} from '@/components/common';
import { PilotLeadStatus } from '@/models/pilot-crm.model';

export const PilotCrmDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [dateRangeOption, setDateRangeOption] = useState<'7d' | '30d' | '90d' | 'all'>('30d');

  const dateRange = useMemo((): DateRange | undefined => {
    if (dateRangeOption === 'all') return undefined;
    const days = dateRangeOption === '7d' ? 7 : dateRangeOption === '90d' ? 90 : 30;
    return {
      start: subDays(new Date(), days),
      end: new Date(),
    };
  }, [dateRangeOption]);

  const { data: pipelineMetrics, isLoading: pipelineLoading } = useQuery({
    queryKey: ['pilot-crm-pipeline-metrics', dateRange],
    queryFn: () => PilotCrmAnalyticsService.getPipelineMetrics(dateRange),
    enabled: !!profile,
  });

  const { data: conversionMetrics, isLoading: conversionLoading } = useQuery({
    queryKey: ['pilot-crm-conversion-metrics', dateRange],
    queryFn: () => PilotCrmAnalyticsService.getConversionMetrics(dateRange),
    enabled: !!profile,
  });

  const { data: activityMetrics, isLoading: activityLoading } = useQuery({
    queryKey: ['pilot-crm-activity-metrics', dateRange],
    queryFn: () => PilotCrmAnalyticsService.getActivityMetrics(dateRange),
    enabled: !!profile,
  });

  const { data: velocityData, isLoading: velocityLoading } = useQuery({
    queryKey: ['pilot-crm-velocity', dateRangeOption],
    queryFn: () => {
      const days = dateRangeOption === '7d' ? 7 : dateRangeOption === '90d' ? 90 : 30;
      return PilotCrmAnalyticsService.getLeadVelocity(days);
    },
    enabled: !!profile && dateRangeOption !== 'all',
  });

  const { data: recentActivity, isLoading: recentActivityLoading } = useQuery({
    queryKey: ['pilot-crm-recent-activity'],
    queryFn: () => PilotCrmAnalyticsService.getRecentActivity(10),
    enabled: !!profile,
  });

  const { data: upcomingTasks, isLoading: upcomingTasksLoading } = useQuery({
    queryKey: ['pilot-crm-upcoming-tasks'],
    queryFn: () => PilotCrmAnalyticsService.getUpcomingTasks(5),
    enabled: !!profile,
  });

  const { data: leads = [] } = useQuery({
    queryKey: ['pilot-crm-leads'],
    queryFn: () => PilotCrmService.getLeads(),
    enabled: !!profile,
  });

  const leadNameMap = useMemo(() => {
    const map = new Map<string, string>();
    leads.forEach((lead) => map.set(lead.id, lead.companyName));
    return map;
  }, [leads]);

  const handleStageClick = (status: PilotLeadStatus) => {
    navigate(`/pilot-crm?status=${status}`);
  };

  const isLoading = pipelineLoading || conversionLoading || activityLoading;

  if (isLoading) {
    return <LoadingSpinner text="Loading dashboard..." fullScreen />;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SectionHeader
        icon={BarChart3}
        title="Pilot CRM Dashboard"
        description="Pipeline overview and performance metrics"
        action={
          <div className="flex items-center gap-3">
            <FormSelect
              label=""
              options={[
                { value: '7d', label: 'Last 7 days' },
                { value: '30d', label: 'Last 30 days' },
                { value: '90d', label: 'Last 90 days' },
                { value: 'all', label: 'All time' },
              ]}
              value={dateRangeOption}
              onChange={(e) => setDateRangeOption(e.target.value as any)}
            />
            <Button variant="primary" onClick={() => navigate('/pilot-crm')}>
              View Leads
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <MetricsCard
          title="Total Leads"
          value={pipelineMetrics?.totalLeads || 0}
          icon={Users}
          color="blue"
        />
        <MetricsCard
          title="Conversion Rate"
          value={`${conversionMetrics?.overallConversion.toFixed(1) || 0}%`}
          subtitle="New to Live"
          icon={TrendingUp}
          color="green"
        />
        <MetricsCard
          title="Pipeline Value"
          value={PilotCrmAnalyticsService.formatCurrency(pipelineMetrics?.totalValue || 0)}
          subtitle="Estimated ARR"
          icon={DollarSign}
          color="purple"
        />
        <MetricsCard
          title="Open Tasks"
          value={activityMetrics?.tasksOpen || 0}
          subtitle={`${activityMetrics?.tasksCompleted || 0} completed`}
          icon={CheckCircle2}
          color="orange"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <Card className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Pipeline Funnel</h3>
          </div>
          {pipelineMetrics && (
            <PipelineFunnel data={pipelineMetrics.byStatus} onStageClick={handleStageClick} />
          )}
        </Card>

        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Activity Summary</h3>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <div className="flex items-center gap-2 text-gray-600">
                <Mail className="w-4 h-4 text-blue-500" />
                <span className="text-sm">Emails Sent</span>
              </div>
              <span className="font-semibold text-gray-900">{activityMetrics?.emailsSent || 0}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <div className="flex items-center gap-2 text-gray-600">
                <Phone className="w-4 h-4 text-green-500" />
                <span className="text-sm">Calls Made</span>
              </div>
              <span className="font-semibold text-gray-900">{activityMetrics?.callsMade || 0}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <div className="flex items-center gap-2 text-gray-600">
                <Calendar className="w-4 h-4 text-purple-500" />
                <span className="text-sm">Meetings Held</span>
              </div>
              <span className="font-semibold text-gray-900">{activityMetrics?.meetingsHeld || 0}</span>
            </div>
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-2 text-gray-600">
                <CheckCircle2 className="w-4 h-4 text-orange-500" />
                <span className="text-sm">Tasks Completed</span>
              </div>
              <span className="font-semibold text-gray-900">{activityMetrics?.tasksCompleted || 0}</span>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Lead Velocity</h3>
          </div>
          {velocityLoading ? (
            <div className="h-64 flex items-center justify-center">
              <LoadingSpinner text="Loading chart..." />
            </div>
          ) : velocityData && velocityData.length > 0 ? (
            <ConversionChart data={velocityData} />
          ) : (
            <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">No velocity data available</p>
            </div>
          )}
        </Card>

        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Lead Sources</h3>
          </div>
          {pipelineMetrics?.bySource ? (
            <SourceBreakdownChart data={pipelineMetrics.bySource} />
          ) : (
            <div className="h-48 flex items-center justify-center bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">No source data available</p>
            </div>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
            <Button
              variant="ghost"
              size="sm"
              icon={ArrowRight}
              onClick={() => navigate('/pilot-crm')}
            >
              View all
            </Button>
          </div>
          {recentActivityLoading ? (
            <LoadingSpinner text="Loading activity..." />
          ) : (
            <ActivityFeed
              activities={recentActivity || []}
              showLeadName
              leadNames={leadNameMap}
              onActivityClick={(activity) => navigate(`/pilot-crm/leads/${activity.leadId}`)}
            />
          )}
        </Card>

        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Upcoming Tasks</h3>
            <Button
              variant="ghost"
              size="sm"
              icon={ArrowRight}
              onClick={() => navigate('/pilot-crm/tasks')}
            >
              View all
            </Button>
          </div>
          {upcomingTasksLoading ? (
            <LoadingSpinner text="Loading tasks..." />
          ) : upcomingTasks && upcomingTasks.length > 0 ? (
            <div className="space-y-3">
              {upcomingTasks.map((task) => (
                <div
                  key={task.id}
                  className="p-3 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-100"
                  onClick={() => navigate(`/pilot-crm/leads/${task.leadId}`)}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{task.title}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {leadNameMap.get(task.leadId) || 'Unknown lead'}
                      </p>
                    </div>
                    <span className="text-xs text-gray-500">
                      {format(task.dueAt, 'MMM d')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <ClipboardList className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">No upcoming tasks</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};
